import { ElementData } from '../types';

/**
 * Calls Gemini API to generate an explanation about a newly created element/compound
 */
// Access API key - vite.config.ts injects GEMINI_API_KEY as __GEMINI_API_KEY__
declare const __GEMINI_API_KEY__: string | undefined;

export async function getElementExplanation(element: ElementData): Promise<string> {
  // Try multiple ways to get the API key:
  // 1. VITE_GEMINI_API_KEY (standard Vite env var, user sets VITE_GEMINI_API_KEY in .env)
  // 2. __GEMINI_API_KEY__ (injected by vite.config.ts from GEMINI_API_KEY)
  const apiKey = (import.meta.env.VITE_GEMINI_API_KEY as string | undefined) ||
                 (typeof __GEMINI_API_KEY__ !== 'undefined' ? __GEMINI_API_KEY__ : undefined);
  
  if (!apiKey) {
    console.warn('Gemini API key not found, using fallback explanation');
    return getFallbackExplanation(element);
  }

  try {
    const prompt = `You are Holo, a knowledgeable chemistry assistant in HoloLab. A student just created ${element.name} (${element.symbol}) by combining elements in a virtual chemistry lab.

Provide a clear, informative explanation (2-3 sentences max) about ${element.name} and its significance. Be precise yet approachable—think scientific clarity with warmth. Focus on real-world relevance and interesting properties.

Element details:
- Name: ${element.name}
- Symbol: ${element.symbol}
- Description: ${element.description || 'N/A'}`;

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-latest:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: prompt
            }]
          }]
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Gemini API error: ${response.status}`);
    }

    const data = await response.json();
    const explanation = data.candidates?.[0]?.content?.parts?.[0]?.text;
    
    if (explanation) {
      // Clean up the response (remove markdown formatting if any)
      return explanation.trim().replace(/\*\*/g, '').replace(/\*/g, '');
    }

    return getFallbackExplanation(element);
  } catch (error) {
    console.error('Error calling Gemini API:', error);
    return getFallbackExplanation(element);
  }
}

/**
 * Calls Gemini API to auto-grade a student's discussion answer based on a rubric/expected points.
 */
export async function autoGradeDiscussionAnswer(
  question: string,
  answer: string,
  expectedKeyPoints?: string[],
  retries: number = 3
): Promise<{ score: number; feedback: string }> {
  // Use same logic to fetch the API key
  const apiKey = (import.meta.env.VITE_GEMINI_API_KEY as string | undefined) ||
                 (typeof __GEMINI_API_KEY__ !== 'undefined' ? __GEMINI_API_KEY__ : undefined);
                 
  if (!apiKey) {
    console.warn('Gemini API key not found, skipping grading.');
    return { score: 100, feedback: "Pending manual review. Auto-grading is disabled (no API key)." };
  }

  if (!answer || answer.trim() === '') {
    return { score: 0, feedback: "No answer provided. Please provide an answer to receive a score." };
  }

  try {
    const keyPointsContext = expectedKeyPoints && expectedKeyPoints.length > 0 
      ? `\nExpected Key Points to cover:\n- ${expectedKeyPoints.join('\n- ')}`
      : "";

    const prompt = `You are a strict, objective chemistry teacher grading a student's post-lab discussion answer.
    
Question: "${question}"${keyPointsContext}
Student's Answer: "${answer}"

Your task:
1. Evaluate the student's answer strictly against the chemical accuracy and expected key points.
2. Hallucinations are strictly prohibited—only evaluate based on standard chemistry principles.
3. Assign a score from 0 to 100 as an integer.
4. Provide a brief feedback explanation (1-2 sentences) justifying the score directly addressed to the student using "You".
5. Return your response in STRICT JSON format with EXACTLY two string keys: "score" (a JSON number) and "feedback" (a JSON string).`;

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-latest:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.1, // Low temperature for factual grounding
            responseMimeType: "application/json"
          }
        }),      
      }
    );

    if (!response.ok) {
      if (response.status === 429 || response.status >= 500) {
          if (retries > 0) {
              console.warn(`Gemini API rate limited (${response.status}). Retrying in 5 seconds...`);
              await new Promise(resolve => setTimeout(resolve, 5000));
              return autoGradeDiscussionAnswer(question, answer, expectedKeyPoints, retries - 1);
          }
      }
      throw new Error(`Gemini API error: ${response.status}`);
    }

    const data = await response.json();
    const rawText = data.candidates?.[0]?.content?.parts?.[0]?.text || "{}";
    
    // Parse the JSON markdown
    const startIdx = rawText.indexOf('{');
    const endIdx = rawText.lastIndexOf('}');
    const cleanText = startIdx !== -1 && endIdx !== -1 ? rawText.substring(startIdx, endIdx + 1) : "{}";
    
    const result = JSON.parse(cleanText);
    
    return {
      score: typeof result.score === 'number' ? result.score : 0,
      feedback: result.feedback || "Unable to determine feedback."
    };

  } catch (err) {
    if (retries > 0) {
        console.warn(`Failed to auto-grade discussion. Retrying...`, err);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return autoGradeDiscussionAnswer(question, answer, expectedKeyPoints, retries - 1);
    }
    console.error('Failed to auto-grade discussion after all retries:', err);
    return { score: 0, feedback: "An error occurred during auto-grading. Needs manual review." };
  }
}

/**
 * Fallback explanation if Gemini API fails or is unavailable
 */
function getFallbackExplanation(element: ElementData): string {
  if (element.atomicNumber === 0) {
    return `Amazing! You've created ${element.name}! This compound has unique properties that make it different from its individual elements.`;
  }
  return `Great discovery! ${element.name} is a fascinating element. ${element.description || 'It has many interesting properties and uses.'}`;
}

